import { isNil, lowerCase } from 'lodash';
import { formatDataTableCellAddress } from '../../../stringification/address';
import { formatDate, getLocalizedDate } from '../../../stringification/dates';
import { formatNumber } from '../../../stringification/numbers';
import { formatOption } from '../../../stringification/options';
import { CompletedTemplateChildConfigs } from '../../../templates/types';
import { Address } from '../../../types/address';
import { StopType } from '../../../types/stop';
import { getArrayValue, getObjectValue, getValue } from '../../getValue';
import {
  CompletedAddressChild,
  CompletedFreightOrderTemplate,
  StopTemplate,
  WeightTemplate,
  WithMetadata,
} from '../types';
import { StandardFreightOrderDriverRequirement } from '../../../enums';

export type Location = {
  address: CompletedAddressChild;
  name: CompletedTemplateChildConfigs;
  locationCode?: CompletedTemplateChildConfigs | undefined;
};

export const getStopCount = (data: CompletedFreightOrderTemplate): number => {
  const allStops = getStopsFromFreightOrder(data);
  return allStops.length;
};

function isValidDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() + 1 === m &&
    date.getUTCDate() === d
  );
}

export const getDateFromStop = (
  stop: WithMetadata<StopTemplate<CompletedTemplateChildConfigs>>,
  formatToken: string = 'M/d/yy'
): string | undefined => {
  if (!stop.date) return;

  const date = getValue(stop.date);
  if (Array.isArray(date)) return;

  const trimmed = date.trim();
  if (!trimmed) return;

  if (!isValidDateString(trimmed)) {
    // eslint-disable-next-line no-console
    console.warn(`getDateFromStop: invalid date value "${date}", skipping`);
    return;
  }

  return formatDate(trimmed, formatToken, true);
};

export const getTimeFromStop = (
  stop: WithMetadata<StopTemplate<CompletedTemplateChildConfigs>>
): string | undefined => {
  if (!stop.time) return;

  const time = getValue(stop.time);
  if (Array.isArray(time)) return;

  if (!time.trim()) return;

  return time;
};

export const getPickupFromFreightOrder = (
  data: CompletedFreightOrderTemplate
): WithMetadata<StopTemplate<CompletedTemplateChildConfigs>> | undefined => {
  const dataValue = data._value;
  const pickupStop = !isNil(dataValue.pickup)
    ? getObjectValue(dataValue.pickup)
    : dataValue.stops._value.find(
        (s) => !isNil(s.stopType) && getObjectValue(s.stopType) === 'PICKUP'
      );
  if (isNil(pickupStop)) return;
  return pickupStop;
};

export const getDeliveryFromFreightOrder = (
  data: CompletedFreightOrderTemplate
): WithMetadata<StopTemplate<CompletedTemplateChildConfigs>> | undefined => {
  const dataValue = data._value;
  const stops = getArrayValue(dataValue.stops);

  // NB: Assuming a single stop per quote
  const delivery = stops[stops.length - 1];

  if (Array.isArray(delivery)) return;

  return delivery;
};

export const getAddressFromLocation = (
  location: Location
): Address | string | undefined => {
  if (Array.isArray(location)) return;
  const address = getValue(location.address);
  if (Array.isArray(address)) return;
  if (typeof address === 'string') return address;
  return address;
};

export const formatPickupCityState = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  if (!data) return;
  const pickup = getPickupFromFreightOrder(data);
  if (isNil(pickup)) return;
  const location = getValue(pickup.location) as Location;
  const address = getAddressFromLocation(location);
  return formatDataTableCellAddress(address);
};

export const formatDeliveryCityState = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  if (!data) return;
  const delivery = getDeliveryFromFreightOrder(data);
  if (!delivery) return;
  const location = getObjectValue(delivery.location) as Location;
  const address = getAddressFromLocation(location);
  return formatDataTableCellAddress(address);
};

export const formatStopsCityState = (
  data?: CompletedFreightOrderTemplate
): string[] | undefined => {
  if (!data) return;
  const stops = getStopsFromFreightOrder(data);
  if (!stops) return;
  const formattedAddresses = stops
    .map((stop) => formatDataTableCellAddress(stop.address))
    .filter((addr): addr is string => typeof addr === 'string');
  return formattedAddresses.length ? formattedAddresses : undefined;
};

export const formatPickupDate = (
  data?: CompletedFreightOrderTemplate,
  formatToken?: string
): string | undefined => {
  if (!data) return;
  const pickup = getPickupFromFreightOrder(data);
  if (isNil(pickup)) return;
  return getDateFromStop(pickup, formatToken);
};

export const formatDeliveryDate = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  if (!data) return;
  const delivery = getDeliveryFromFreightOrder(data);
  if (!delivery) return;
  return getDateFromStop(delivery);
};

const formatValues = (values: string | string[]): string | undefined => {
  if (!values) return;
  if (Array.isArray(values)) {
    return values.map((e) => formatOption(e)).join(', ');
  }
  return formatOption(values);
};

export const getEquipmentTypesFromFreightOrder = (
  data?: CompletedFreightOrderTemplate
): string[] => {
  const equipmentType = data?._value?.equipmentType;
  if (!equipmentType) return [];
  const value = getValue(equipmentType);
  if (typeof value === 'string') return [value];
  return value;
};

export const getAbbreviatedFreightMode = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  const freightMode = getFreightMode(data);
  if (!freightMode) return;
  if (freightMode === 'Truckload') return 'TL';
  if (freightMode === 'LTL') return 'LTL';

  return freightMode;
};

export const getFreightMode = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  const freightMode = data?._value?.freightMode;
  if (!freightMode) return undefined;
  const freightModeObj = getObjectValue(freightMode);
  if (!freightModeObj) return undefined;
  const value = getValue(freightModeObj?.mode);
  if (typeof value === 'string') {
    if (value === 'TRUCKLOAD') return 'Truckload';
    if (value === 'LTL') return 'LTL';
    return value;
  }
  return undefined;
};

export const getAndFormatEquipmentType = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  const equipmentTypes = getEquipmentTypesFromFreightOrder(data);
  return equipmentTypes.map(formatOption).join(', ') || undefined;
};

export const getCommodityFromFreightOrder = (
  data?: CompletedFreightOrderTemplate
): string | string[] | undefined => {
  const commodity = data?._value?.commodity;
  if (!commodity) return;
  return formatValues(getValue(commodity));
};

export const getWeightFromFreightOrder = (
  data?: CompletedFreightOrderTemplate
): string | undefined => {
  const weight = data?._value?.weight;
  if (!weight) return;
  const weightSection = getValue(
    weight
  ) as WeightTemplate<CompletedTemplateChildConfigs>;
  const weightValue = getValue(weightSection.weight) as string;
  const weightUnits = getValue(weightSection.units) as string;
  if (!weightValue) return;
  return `${formatNumber(parseFloat(weightValue), 7)} ${lowerCase(
    weightUnits
  )}`;
};

export function getStopsFromFreightOrder(data: CompletedFreightOrderTemplate) {
  const hardcodedPickup = data?._value?.pickup?._value;
  const deliveries = getArrayValue(data?._value?.stops);
  const stops = !isNil(hardcodedPickup)
    ? [hardcodedPickup, ...deliveries]
    : deliveries;
  return stops.map((stop, idx, arr) => getStopData(stop, idx, arr.length));
}

function getStopData(
  stop: WithMetadata<StopTemplate<CompletedTemplateChildConfigs>>,
  idx: number,
  length: number
) {
  const rawDateString = stop.date ? (getValue(stop.date) as string) : undefined;
  const trimmedDateString = rawDateString?.trim();
  const dateString =
    trimmedDateString && isValidDateString(trimmedDateString)
      ? trimmedDateString
      : undefined;
  if (trimmedDateString && !dateString) {
    // eslint-disable-next-line no-console
    console.warn(
      `getStopData: invalid date value "${rawDateString}", skipping`
    );
  }
  const date = dateString ? getLocalizedDate(dateString) : undefined;
  const time = stop.time ? (getValue(stop.time) as string) : undefined;
  const location = getValue(stop.location) as Location;
  const address = extractAddress(location);
  const type = determineStopType(idx, length);
  return {
    address,
    date,
    dateString,
    time,
    type,
  };
}

function determineStopType(idx: number, length: number) {
  if (idx === 0) {
    return 'PICKUP' as StopType;
  } else if (idx === length - 1) {
    return 'DELIVERY' as StopType;
  } else {
    return 'NONE' as StopType;
  }
}

function extractAddress(location: Location) {
  return getValue(location.address) as Address;
}

export const getDriverRequirementFromFreightOrder = (
  data?: CompletedFreightOrderTemplate | null
): StandardFreightOrderDriverRequirement => {
  const driverRequirement = data?._value?.driverRequirement;
  if (!driverRequirement) return 'NO_PREFERENCE';

  switch (getObjectValue(driverRequirement)) {
    case 'TEAM':
      return 'TEAM';
    case 'SOLO':
      return 'SOLO';
    default:
      return 'NO_PREFERENCE';
  }
};
